import { OrniTransportError } from "../transport.ts";
import type {
  AutoContinuePolicyDto,
  ProcessAttemptDto,
  ProviderFactDto,
  ReceiptOutcomeDto,
  SessionDto,
  SessionSnapshotDto,
  SessionsSnapshotDto,
} from "./session-dtos.ts";

export function decodeSessionsSnapshot(value: unknown): SessionsSnapshotDto {
  const root = wireRecord(value, "SessionsSnapshot");
  return {
    registry_revision: integer(root.registry_revision, "registry_revision"),
    sessions: wireArray(root.sessions, "sessions").map((item, index) =>
      decodeSessionSnapshot(item, `sessions[${index}]`),
    ),
  };
}

export function decodeReceiptOutcome(value: unknown): ReceiptOutcomeDto {
  const source = wireRecord(value, "ReceiptOutcome");
  const error = source.error === undefined || source.error === null
    ? undefined
    : wireRecord(source.error, "ReceiptOutcome.error");
  const entityRevision = optionalInteger(
    source.entity_revision,
    "ReceiptOutcome.entity_revision",
  );
  return {
    receipt_id: text(source.receipt_id, "ReceiptOutcome.receipt_id"),
    command_id: text(source.command_id, "ReceiptOutcome.command_id"),
    ordinal: integer(source.ordinal, "ReceiptOutcome.ordinal"),
    request_id: text(source.request_id, "ReceiptOutcome.request_id"),
    status: oneOf(
      source.status,
      [
        "received",
        "accepted",
        "applied",
        "rejected",
        "indeterminate",
        "expired",
        "cancelled",
      ] as const,
      "ReceiptOutcome.status",
    ),
    ...(source.result === undefined ? {} : { result: source.result }),
    ...(error
      ? {
          error: {
            code: text(error.code, "ReceiptOutcome.error.code"),
            message: text(error.message, "ReceiptOutcome.error.message"),
            retryable: boolean(
              error.retryable,
              "ReceiptOutcome.error.retryable",
            ),
          },
        }
      : {}),
    ...(entityRevision === undefined ? {} : { entity_revision: entityRevision }),
    recorded_at: timestamp(source.recorded_at, "ReceiptOutcome.recorded_at"),
  };
}

export function wireRecord(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw contractError(path, "object");
  }
  return value as Record<string, unknown>;
}

export function wireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw contractError(path, "array");
  return value;
}

export function decodeSessionSnapshot(value: unknown, path = "SessionSnapshot"): SessionSnapshotDto {
  const root = wireRecord(value, path);
  return {
    session: decodeSession(root.session, `${path}.session`),
    ...(root.current_attempt === undefined || root.current_attempt === null
      ? {}
      : {
          current_attempt: decodeProcessAttempt(
            root.current_attempt,
            `${path}.current_attempt`,
          ),
        }),
    provider_facts: wireArray(root.provider_facts, `${path}.provider_facts`).map(
      (item, index) =>
        decodeProviderFact(item, `${path}.provider_facts[${index}]`),
    ),
  };
}

function decodeSession(value: unknown, path: string): SessionDto {
  const source = wireRecord(value, path);
  const provider = wireRecord(
    source.provider_selection,
    `${path}.provider_selection`,
  );
  const workspace = wireRecord(source.workspace, `${path}.workspace`);
  const executableHint = optionalText(
    provider.executable_hint,
    `${path}.provider_selection.executable_hint`,
  );
  const agentId = optionalText(
    provider.agent_id ?? provider.agentId,
    `${path}.provider_selection.agent_id`,
  );
  const cliVariantId = optionalText(
    provider.cli_variant_id ?? provider.cliVariantId,
    `${path}.provider_selection.cli_variant_id`,
  );
  const currentAttemptId = optionalText(
    source.current_attempt_id,
    `${path}.current_attempt_id`,
  );
  return {
    session_id: text(source.session_id, `${path}.session_id`),
    title: text(source.title, `${path}.title`),
    provider_selection: {
      provider_id: text(
        provider.provider_id,
        `${path}.provider_selection.provider_id`,
      ),
      ...(agentId === undefined ? {} : { agent_id: agentId }),
      ...(cliVariantId === undefined ? {} : { cli_variant_id: cliVariantId }),
      integration_mode: text(
        provider.integration_mode,
        `${path}.provider_selection.integration_mode`,
      ),
      ...(executableHint === undefined ? {} : { executable_hint: executableHint }),
    },
    workspace: {
      workspace_id: text(
        workspace.workspace_id,
        `${path}.workspace.workspace_id`,
      ),
    },
    permission_mode: oneOf(
      source.permission_mode,
      ["read-only", "workspace-write", "full-access"] as const,
      `${path}.permission_mode`,
    ),
    management_mode: oneOf(
      source.management_mode,
      ["native"] as const,
      `${path}.management_mode`,
    ),
    auto_continue: decodeAutoContinue(
      source.auto_continue,
      `${path}.auto_continue`,
    ),
    desired_state: oneOf(
      source.desired_state,
      ["stopped", "running", "deleted"] as const,
      `${path}.desired_state`,
    ),
    ...(currentAttemptId === undefined
      ? {}
      : { current_attempt_id: currentAttemptId }),
    next_generation: integer(source.next_generation, `${path}.next_generation`),
    created_at: timestamp(source.created_at, `${path}.created_at`),
    updated_at: timestamp(source.updated_at, `${path}.updated_at`),
    revision: integer(source.revision, `${path}.revision`),
  };
}

function decodeAutoContinue(value: unknown, path: string): AutoContinuePolicyDto {
  const source = wireRecord(value, path);
  const recoveryStartedAt = optionalTimestamp(
    source.recovery_started_at,
    `${path}.recovery_started_at`,
  );
  const lastRecoveryError = optionalText(
    source.last_recovery_error,
    `${path}.last_recovery_error`,
  );
  return {
    enabled: boolean(source.enabled, `${path}.enabled`),
    recovery_enabled: boolean(
      source.recovery_enabled,
      `${path}.recovery_enabled`,
    ),
    retry_window_seconds: integer(
      source.retry_window_seconds,
      `${path}.retry_window_seconds`,
    ),
    retry_forever: boolean(source.retry_forever, `${path}.retry_forever`),
    retry_interval_seconds: integer(
      source.retry_interval_seconds,
      `${path}.retry_interval_seconds`,
    ),
    attempts_used: integer(source.attempts_used, `${path}.attempts_used`),
    ...(recoveryStartedAt === undefined
      ? {}
      : { recovery_started_at: recoveryStartedAt }),
    ...(lastRecoveryError === undefined
      ? {}
      : { last_recovery_error: lastRecoveryError }),
    recovery_history: wireArray(
      source.recovery_history,
      `${path}.recovery_history`,
    ).map((item, index) => decodeRecoveryRecord(item, `${path}.recovery_history[${index}]`)),
  };
}

function decodeRecoveryRecord(value: unknown, path: string) {
  const source = wireRecord(value, path);
  const detail = optionalText(source.detail, `${path}.detail`);
  const completedAt = optionalTimestamp(source.completed_at, `${path}.completed_at`);
  return {
    cause: text(source.cause, `${path}.cause`),
    error: text(source.error, `${path}.error`),
    attempted_at: timestamp(source.attempted_at, `${path}.attempted_at`),
    attempt_number: integer(source.attempt_number, `${path}.attempt_number`),
    outcome: oneOf(
      source.outcome,
      ["scheduled", "dispatched", "succeeded", "cancelled", "failed"] as const,
      `${path}.outcome`,
    ),
    ...(detail === undefined ? {} : { detail }),
    ...(completedAt === undefined ? {} : { completed_at: completedAt }),
  };
}

function decodeProcessAttempt(value: unknown, path: string): ProcessAttemptDto {
  const source = wireRecord(value, path);
  const pid = optionalInteger(source.pid, `${path}.pid`);
  const startedAt = optionalTimestamp(source.started_at, `${path}.started_at`);
  const endedAt = optionalTimestamp(source.ended_at, `${path}.ended_at`);
  return {
    process_attempt_id: text(
      source.process_attempt_id,
      `${path}.process_attempt_id`,
    ),
    session_id: text(source.session_id, `${path}.session_id`),
    generation: integer(source.generation, `${path}.generation`),
    integration_mode: text(source.integration_mode, `${path}.integration_mode`),
    process_state: oneOf(
      source.process_state,
      [
        "planned",
        "spawning",
        "running",
        "stopping",
        "exited",
        "failed_to_start",
        "lost",
      ] as const,
      `${path}.process_state`,
    ),
    provider_readiness: oneOf(
      source.provider_readiness,
      ["unknown", "probing", "ready", "degraded", "unavailable"] as const,
      `${path}.provider_readiness`,
    ),
    ...(pid === undefined ? {} : { pid }),
    ...(startedAt === undefined ? {} : { started_at: startedAt }),
    ...(endedAt === undefined ? {} : { ended_at: endedAt }),
    revision: integer(source.revision, `${path}.revision`),
  };
}

function decodeProviderFact(value: unknown, path: string): ProviderFactDto {
  const source = wireRecord(value, path);
  const attributes = wireRecord(source.attributes, `${path}.attributes`);
  return {
    fact_id: text(source.fact_id, `${path}.fact_id`),
    session_id: text(source.session_id, `${path}.session_id`),
    process_attempt_id: text(
      source.process_attempt_id,
      `${path}.process_attempt_id`,
    ),
    generation: integer(source.generation, `${path}.generation`),
    kind: text(source.kind, `${path}.kind`),
    attributes: Object.fromEntries(
      Object.entries(attributes).map(([key, item]) => [
        key,
        text(item, `${path}.attributes.${key}`),
      ]),
    ),
    observed_at: timestamp(source.observed_at, `${path}.observed_at`),
  };
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string") throw contractError(path, "string");
  return value;
}

function optionalText(value: unknown, path: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  return text(value, path);
}

function integer(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw contractError(path, "non-negative safe integer");
  }
  return value;
}

function optionalInteger(value: unknown, path: string): number | undefined {
  if (value === undefined || value === null) return undefined;
  return integer(value, path);
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw contractError(path, "boolean");
  return value;
}

function timestamp(value: unknown, path: string): string {
  const result = text(value, path);
  if (!Number.isFinite(Date.parse(result))) throw contractError(path, "ISO timestamp");
  return result;
}

function optionalTimestamp(value: unknown, path: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  return timestamp(value, path);
}

function oneOf<const T extends readonly string[]>(
  value: unknown,
  values: T,
  path: string,
): T[number] {
  if (typeof value !== "string" || !values.includes(value)) {
    throw contractError(path, values.join(" | "));
  }
  return value as T[number];
}

function contractError(path: string, expected: string): OrniTransportError {
  return new OrniTransportError(
    "CONTRACT_INVALID",
    `Invalid local Daemon response at ${path}; expected ${expected}.`,
  );
}
